/**
 * Where the selection is: its model-space box, what a zoomed-in view pans to
 * reveal, and its center, what the editor's measure bars gravitate toward.
 */

import {
  alcoveRingVerts,
  edgeSegments,
  type FloorPlanRoom,
  freeElementRing,
  geometryBounds,
  isHorizontalEdge,
  type PlanBounds,
  type Point,
  primaryEdgeLineFrame,
  resolveLineRef,
  worldOnLine,
} from '@kaiizen/kai-floor-plan';
import { chamferData, displayShape } from '../editorGeometry';
import type { EditorMutableState } from '../editorState';
import { type DimBarSides, dimBarSidesFor } from './measureBarLayout';

const boundsOfPoints = (points: readonly Point[]): PlanBounds | null =>
  points.length
    ? {
      minX: Math.min(...points.map(p => p.x)),
      minY: Math.min(...points.map(p => p.y)),
      maxX: Math.max(...points.map(p => p.x)),
      maxY: Math.max(...points.map(p => p.y)),
    }
    : null;

/**
 * The model-space box of what's selected: the region a zoomed-in view pans to
 * reveal after the room's boundary changes (a new alcove, a resized wall).
 * Null for the whole room or nothing: there is no one place to look.
 */
export function selectionBounds(state: EditorMutableState): PlanBounds | null {
  const room = displayShape(state);
  const selection = state.selection;
  if (selection.kind === 'alcove' || selection.kind === 'alcoveWall' || selection.kind === 'alcoveCorner') {
    const segment = edgeSegments(room, selection.edge)[selection.index];
    if (!segment) return null;
    if (segment.edgeSegmentType === 'alcove') return boundsOfPoints(alcoveRingVerts(room, selection.edge, segment));
    const lineFrame = primaryEdgeLineFrame(room, selection.edge);
    return boundsOfPoints([worldOnLine(lineFrame, segment.position, 0), worldOnLine(lineFrame, segment.position + segment.width, 0)]);
  }
  if (selection.kind === 'lineSeg') {
    const line = resolveLineRef(room, selection.ref);
    const segment = line?.segments[selection.si];
    if (!line || !segment) return null;
    return boundsOfPoints([worldOnLine(line.lf, segment.position, 0), worldOnLine(line.lf, segment.position + segment.width, 0)]);
  }
  if (selection.kind === 'inner' || selection.kind === 'innerCorner') {
    const element = room.freeElements?.[selection.index];
    return element ? boundsOfPoints(freeElementRing(element)) : null;
  }
  if (selection.kind === 'edge') {
    const lineFrame = primaryEdgeLineFrame(room, selection.edge);
    return boundsOfPoints([worldOnLine(lineFrame, 0, 0), worldOnLine(lineFrame, lineFrame.length, 0)]);
  }
  if (selection.kind === 'corner') {
    const chamfer = chamferData(state, selection);
    if (!chamfer) return null;
    return boundsOfPoints([
      chamfer.corner,
      { x: chamfer.corner.x + chamfer.sx * chamfer.ch.dx, y: chamfer.corner.y + chamfer.sy * chamfer.ch.dy },
    ]);
  }
  return null;
}

/**
 * The model-space center of what's selected, the point the dim bars
 * gravitate toward: an alcove's or a free element's middle, a wall's or a
 * segment's midpoint, a room corner's own point. Not the center of
 * `selectionBounds`: a cut corner or a rotated, chamfered element would land
 * on a different side. Null for the room or nothing.
 */
function selectionAnchor(state: EditorMutableState, room: FloorPlanRoom): Point | null {
  const selection = state.selection;
  if (selection.kind === 'alcove' || selection.kind === 'alcoveWall' || selection.kind === 'alcoveCorner') {
    const alcove = edgeSegments(room, selection.edge)[selection.index];
    if (!alcove) return null;
    const lineFrame = primaryEdgeLineFrame(room, selection.edge);
    return worldOnLine(lineFrame, alcove.position + alcove.width / 2, (alcove.depth ?? 0) / 2);
  }
  if (selection.kind === 'inner' || selection.kind === 'innerCorner') {
    const element = room.freeElements?.[selection.index];
    if (!element) return null;
    return { x: element.x + element.width / 2, y: element.y + element.length / 2 };
  }
  if (selection.kind === 'edge') {
    const lineFrame = primaryEdgeLineFrame(room, selection.edge);
    return worldOnLine(lineFrame, lineFrame.length / 2, 0);
  }
  if (selection.kind === 'corner') {
    return {
      x: selection.corner === 'backRightCorner' || selection.corner === 'frontRightCorner' ? room.width : 0,
      y: selection.corner === 'frontLeftCorner' || selection.corner === 'frontRightCorner' ? room.length : 0,
    };
  }
  if (selection.kind === 'lineSeg') {
    const line = resolveLineRef(room, selection.ref);
    const segment = line?.segments[selection.si];
    if (!line || !segment) return null;
    return worldOnLine(line.lf, segment.position + segment.width / 2, 0);
  }
  return null;
}

/**
 * Which side of the room each dim-bar family renders on: the pair nearest
 * the current selection, so the measurements sit next to what's being
 * edited. The add-segment icon sits directly on a selected edge with a gap,
 * where the nearest-side rule would stack the same-orientation bars on top
 * of it, so that one axis flips to the far side while the icon shows.
 */
export function dimBarSides(state: EditorMutableState): DimBarSides {
  const room = displayShape(state);
  const sides = dimBarSidesFor(selectionAnchor(state, room), geometryBounds(room));
  const selection = state.selection;
  if (selection.kind !== 'edge' || !state.edgeGap) return sides;
  return isHorizontalEdge(selection.edge)
    ? { ...sides, horizontal: sides.horizontal === 'top' ? 'bottom' : 'top' }
    : { ...sides, vertical: sides.vertical === 'left' ? 'right' : 'left' };
}
